import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import apiClient from '../../utils/apiClient';
import './TestCoverageAdvisor.css';

/**
 * TestCoverageAdvisor - Analyse de couverture de tests
 * 
 * Identifie les zones non testées ou sous-testées du système et génère
 * automatiquement des recommandations de tests via IA.
 * 
 * Fonctionnalités :
 * - Score de couverture global (%)
 * - Identification des gaps (nœuds avec <3 tests)
 * - Score de risque par nœud (criticité)
 * - Génération automatique de tests par IA
 * - Recommandations personnalisées
 */
const TestCoverageAdvisor = ({ onNavigateToTestGenerator }) => {
  const { t } = useTranslation();
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);

  useEffect(() => {
    loadCoverageAnalysis();
  }, []);

  const loadCoverageAnalysis = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/api/smart-tools/analyze-coverage');
      setCoverage(data);
      
      if (data.gaps.length > 0) {
        toast.warning(t('smartTools.coverageAdvisor.gapsFound', { count: data.gaps.length }));
      } else {
        toast.success(t('smartTools.coverageAdvisor.fullCoverage'));
      }
    } catch (error) {
      console.error('Error loading coverage:', error);
      toast.error(t('smartTools.coverageAdvisor.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTests = (node) => {
    // Naviguer vers FeatureManager avec modal TestGeneration ouverte pour ce nœud
    if (onNavigateToTestGenerator) {
      onNavigateToTestGenerator(node.id, node.name);
    } else {
      toast.warning(t('smartTools.coverageAdvisor.navigationNotAvailable') || 'Navigation non disponible');
    }
  };

  const getRiskColor = (riskScore) => {
    if (riskScore >= 8) return '#dc2626'; // Rouge - Critique
    if (riskScore >= 6) return '#ea580c'; // Orange - Élevé
    if (riskScore >= 4) return '#f59e0b'; // Jaune - Moyen
    return '#10b981'; // Vert - Faible
  };

  const getRiskLabel = (riskScore) => {
    if (riskScore >= 8) return t('smartTools.coverageAdvisor.criticalRisk');
    if (riskScore >= 6) return t('smartTools.coverageAdvisor.highRisk');
    if (riskScore >= 4) return t('smartTools.coverageAdvisor.mediumRisk');
    return t('smartTools.coverageAdvisor.lowRisk');
  };

  const getCoverageColor = (score) => {
    if (score >= 80) return '#10b981'; // Vert
    if (score >= 60) return '#f59e0b'; // Jaune
    if (score >= 40) return '#ea580c'; // Orange
    return '#dc2626'; // Rouge
  };

  if (loading) {
    return (
      <div className="test-coverage-advisor">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('smartTools.coverageAdvisor.analyzing')}</p>
        </div>
      </div>
    );
  }

  if (!coverage) {
    return null;
  }

  const coveragePercentage = coverage.score;

  return (
    <div className="test-coverage-advisor">
      <div className="advisor-header">
        <div className="header-content">
          <h2>📊 {t('smartTools.coverageAdvisor.title')}</h2>
          <p className="header-description">{t('smartTools.coverageAdvisor.description')}</p>
        </div>
        
        <button
          onClick={loadCoverageAnalysis}
          disabled={loading}
          className="refresh-button"
        >
          🔄 {t('smartTools.coverageAdvisor.refresh')}
        </button>
      </div>

      <div className="coverage-stats">
        <div className="stat-card coverage-score">
          <div
            className="score-circle"
            style={{ borderColor: getCoverageColor(coveragePercentage) }}
          >
            <span className="score-value" style={{ color: getCoverageColor(coveragePercentage) }}>
              {coveragePercentage.toFixed(1)}%
            </span>
            <span className="score-label">{t('smartTools.coverageAdvisor.coverage')}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📝</div>
          <div className="stat-content">
            <div className="stat-value">{coverage.totalNodes}</div>
            <div className="stat-label">{t('smartTools.coverageAdvisor.totalNodes')}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{coverage.testedNodes}</div>
            <div className="stat-label">{t('smartTools.coverageAdvisor.testedNodes')}</div>
          </div>
        </div>

        <div className="stat-card stat-warning">
          <div className="stat-icon">⚠️</div>
          <div className="stat-content">
            <div className="stat-value">{coverage.gaps.length}</div>
            <div className="stat-label">{t('smartTools.coverageAdvisor.gaps')}</div>
          </div>
        </div>
      </div>

      {coverage.gaps.length > 0 && (
        <div className="gaps-section">
          <div className="section-header">
            <h3>{t('smartTools.coverageAdvisor.gapsTitle')}</h3>
            <p>{t('smartTools.coverageAdvisor.gapsDescription')}</p>
          </div>

          <div className="gaps-list">
            {coverage.gaps.map((gap) => (
              <div key={gap.id} className="gap-card">
                <div className="gap-header">
                  <div className="gap-info">
                    <span className="node-type-badge">{gap.type}</span>
                    <h4>{gap.name}</h4>
                  </div>
                  <div
                    className="risk-badge"
                    style={{ backgroundColor: getRiskColor(gap.riskScore) }}
                  >
                    {getRiskLabel(gap.riskScore)} ({gap.riskScore}/10)
                  </div>
                </div>

                <p className="gap-description">
                  {gap.description || t('smartTools.coverageAdvisor.noDescription')}
                </p>

                <div className="gap-stats">
                  <span className="gap-stat">
                    📊 {t('smartTools.coverageAdvisor.currentTests')}: {gap.currentTests}
                  </span>
                  <span className="gap-stat">
                    🎯 {t('smartTools.coverageAdvisor.recommendedTests')}: {gap.recommendedTests}
                  </span>
                </div>

                <div className="suggestions-section">
                  <h5>{t('smartTools.coverageAdvisor.suggestions')}</h5>
                  <ul className="suggestions-list">
                    {gap.suggestions.map((suggestion, idx) => (
                      <li key={idx}>{suggestion}</li>
                    ))}
                  </ul>
                </div>

                <div className="gap-actions">
                  <button
                    onClick={() => setSelectedNode(selectedNode === gap.id ? null : gap.id)}
                    className="details-button"
                  >
                    {selectedNode === gap.id ? '▼' : '▶'} {t('smartTools.coverageAdvisor.details')}
                  </button>
                  <button
                    onClick={() => handleGenerateTests(gap)}
                    disabled={generating}
                    className="generate-button"
                  >
                    ✨ {t('smartTools.coverageAdvisor.generateTests')}
                  </button>
                </div>

                {selectedNode === gap.id && (
                  <div className="node-details">
                    <h5>{t('smartTools.coverageAdvisor.nodeDetails')}</h5>
                    <div className="details-grid">
                      {Object.entries(gap).map(([key, value]) => (
                        key !== 'embedding' && key !== 'suggestions' && (
                          <div key={key} className="detail-item">
                            <strong>{key}:</strong> {JSON.stringify(value)}
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {coverage.gaps.length === 0 && (
        <div className="success-state">
          <div className="success-icon">🎉</div>
          <h3>{t('smartTools.coverageAdvisor.excellentCoverage')}</h3>
          <p>{t('smartTools.coverageAdvisor.excellentDescription')}</p>
        </div>
      )}
    </div>
  );
};

export default TestCoverageAdvisor;
